/**
 * Atom: TranslateButton
 *
 * Shows a small 🌐 translate icon when:
 *  - The user's i18n language is non-English, AND
 *  - The field has content
 *
 * On click: POSTs to /api/v1/sage/translate and calls onTranslated(result).
 *
 * Usage:
 *   <TranslateButton
 *     text={form.description}
 *     onTranslated={(t) => setForm(f => ({ ...f, description: t }))}
 *   />
 */
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { tokens } from '@/design-system/tokens'
import { authedFetch, getToken } from '@/lib/api';
import { Spinner } from './Spinner'

interface TranslateButtonProps {
  text: string
  onTranslated: (translatedText: string) => void
  /** Override target language (defaults to i18n.language) */
  targetLanguage?: string
  style?: React.CSSProperties
}

export const TranslateButton: React.FC<TranslateButtonProps> = ({
  text,
  onTranslated,
  targetLanguage,
  style,
}) => {
  const { i18n } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const lang = targetLanguage || i18n.language || 'en'

  // Only show when: non-English language AND field has content
  if (lang === 'en' || !text || !text.trim()) return null

  const handleTranslate = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await authedFetch('/api/v1/sage/translate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text, target_language: lang }),
      }, { redirectOnAuthFailure: false })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error((err as { error?: string }).error || 'Translation failed')
      }
      const data = await res.json() as { translated_text: string }
      onTranslated(data.translated_text)
    } catch (e) {
      setError((e as Error).message || 'Translation error')
      setTimeout(() => setError(null), 3000)
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      type="button"
      title={`Translate to ${lang.toUpperCase()}`}
      onClick={handleTranslate}
      disabled={loading}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 6px',
        background: 'transparent',
        border: `1px solid ${error ? tokens.color.feedback.error : tokens.color.border.default}`,
        borderRadius: tokens.radius.sm,
        cursor: loading ? 'wait' : 'pointer',
        color: error ? tokens.color.feedback.error : tokens.color.text.muted,
        fontSize: '11px',
        fontFamily: tokens.typography.fontFamily.sans,
        transition: 'all 0.15s',
        verticalAlign: 'middle',
        ...style,
      }}
      onMouseEnter={e => {
        if (!loading && !error) {
          (e.currentTarget as HTMLButtonElement).style.borderColor = tokens.color.brand.gold
          ;(e.currentTarget as HTMLButtonElement).style.color = tokens.color.brand.gold
        }
      }}
      onMouseLeave={e => {
        if (!error) {
          (e.currentTarget as HTMLButtonElement).style.borderColor = tokens.color.border.default
          ;(e.currentTarget as HTMLButtonElement).style.color = tokens.color.text.muted
        }
      }}
    >
      {loading ? (
        <Spinner size={11} color={tokens.color.brand.gold} />
      ) : (
        <span style={{ fontSize: '13px', lineHeight: 1 }}>🌐</span>
      )}
      {error ? (
        <span>{error.slice(0, 20)}</span>
      ) : (
        <span>{loading ? 'Translating…' : lang.toUpperCase()}</span>
      )}
    </button>
  )
}
